/**
 * Helpers for secondary booking flights (domestic / international).
 * Arrival and Departure can have different dates (same rules as main flight).
 * International flights may also have only Arrival, only Departure, or both.
 */

function filled(...values) {
  return values.some((v) => v != null && String(v).trim() !== "");
}

export function hasArrival(flight) {
  if (!flight) return false;
  return filled(flight.arrive_date, flight.arrive_airport, flight.arrive_flight_num, flight.arrive_time);
}

export function hasDeparture(flight) {
  if (!flight) return false;
  return filled(flight.depart_date, flight.depart_airport, flight.depart_flight_num, flight.depart_time);
}

export function isFlightEmpty(flight) {
  return !hasArrival(flight) && !hasDeparture(flight);
}

export function arrivalDate(flight) {
  if (flight.arrive_date) return flight.arrive_date;
  // Legacy shared date when arrival side has content
  if (filled(flight.arrive_airport, flight.arrive_time, flight.arrive_flight_num)) {
    return flight.date || "";
  }
  return "";
}

export function departureDate(flight) {
  if (flight.depart_date) return flight.depart_date;
  if (filled(flight.depart_airport, flight.depart_time, flight.depart_flight_num)) {
    return flight.date || "";
  }
  return "";
}

export function arrivalFlightNum(flight) {
  if (flight.arrive_flight_num) return flight.arrive_flight_num;
  if (filled(flight.arrive_airport, flight.arrive_time, flight.arrive_date)) {
    return flight.flight_num || "";
  }
  return "";
}

export function departureFlightNum(flight) {
  if (flight.depart_flight_num) return flight.depart_flight_num;
  if (filled(flight.depart_airport, flight.depart_time, flight.depart_date)) {
    return flight.flight_num || "";
  }
  return "";
}

/** Departure date picker lower bound — same rule as main flight departWall. */
export function departDateWall(flight, datewall) {
  return arrivalDate(flight) || datewall;
}

export function createEmptyFlight(flightType) {
  if (flightType === "INTERNATIONAL") {
    return {
      flight_type: flightType,
      arrive_date: "",
      arrive_airport: "",
      arrive_flight_num: "",
      arrive_time: "",
      depart_date: "",
      depart_airport: "",
      depart_flight_num: "",
      depart_time: "",
    };
  }
  // Domestic: independent arrival/departure dates; shared flight #
  return {
    flight_type: flightType,
    arrive_date: "",
    arrive_airport: "",
    arrive_time: "",
    depart_date: "",
    depart_airport: "",
    depart_time: "",
    flight_num: "",
  };
}

/** Normalize stored flight for the dialog (legacy shared date → independent fields). */
export function normalizeFlight(item, flightType) {
  if (flightType === "INTERNATIONAL" || item.flight_type === "INTERNATIONAL") {
    const arriveSide = filled(item.arrive_airport, item.arrive_time, item.arrive_date, item.arrive_flight_num);
    const departSide = filled(item.depart_airport, item.depart_time, item.depart_date, item.depart_flight_num);

    return {
      flight_type: "INTERNATIONAL",
      arrive_date: item.arrive_date || (arriveSide ? item.date : "") || "",
      arrive_airport: item.arrive_airport || "",
      arrive_flight_num: item.arrive_flight_num || (arriveSide ? item.flight_num : "") || "",
      arrive_time: item.arrive_time || "",
      depart_date: item.depart_date || (departSide ? item.date : "") || "",
      depart_airport: item.depart_airport || "",
      depart_flight_num: item.depart_flight_num || (departSide ? item.flight_num : "") || "",
      depart_time: item.depart_time || "",
    };
  }

  // Domestic (and legacy flights without flight_type)
  return {
    flight_type: item.flight_type || "DOMESTIC",
    arrive_date: item.arrive_date || item.date || "",
    arrive_airport: item.arrive_airport || "",
    arrive_time: item.arrive_time || "",
    depart_date: item.depart_date || item.date || "",
    depart_airport: item.depart_airport || "",
    depart_time: item.depart_time || "",
    flight_num: item.flight_num || item.arrive_flight_num || item.depart_flight_num || "",
  };
}

// Back-compat alias
export function normalizeInternationalFlight(item) {
  return normalizeFlight(item, "INTERNATIONAL");
}
